// Traffic & ETA helpers.
//
// IMPORTANT — what this can and cannot do:
//   * OSRM/Valhalla return *free-flow* durations: the time a road network
//     allows with no congestion. No keyless public routing service exposes
//     live traffic (OpenRouteService, TomTom, HERE and Mapbox all require an
//     API key).
//   * So we estimate congestion from what we can actually measure: the
//     device's own GPS speed while travelling, compared against the free-flow
//     time for the same corridor. Samples are persisted locally and decay
//     over time so stale data fades out.
//   * Everything here is clearly labelled as an *estimate*; it never claims
//     to be official traffic data.

const PROFILE_KEY = 'leadmap3d.speedProfile.v1';
const SESSION_KEY = 'leadmap3d.trafficSession.v1';

/** Half-life for a speed sample, in hours. */
const HALF_LIFE_HOURS = 6;

export interface SpeedSample {
  /** km/h observed */
  speed: number;
  /** epoch ms */
  t: number;
  /** coarse road class key, e.g. 'motorway' | 'urban' | 'rural' | 'unknown' */
  road: string;
}

interface SpeedProfile {
  samples: SpeedSample[];
}

export interface CongestionEstimate {
  /** 1 = free flow, >1 = slower than free flow */
  factor: number;
  /** 'clear' | 'moderate' | 'heavy' | 'unknown' */
  level: 'clear' | 'moderate' | 'heavy' | 'unknown';
  label: string;
  /** number of observations backing the estimate */
  observations: number;
  /** observed mean speed in km/h, or null */
  observedSpeed: number | null;
}

/* ------------------------------------------------------------------ */
/*  Persistence                                                        */
/* ------------------------------------------------------------------ */

function readProfile(): SpeedProfile {
  if (typeof localStorage === 'undefined') return { samples: [] };
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return { samples: [] };
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.samples)) return parsed as SpeedProfile;
  } catch {
    /* ignore corrupt data */
  }
  return { samples: [] };
}

function writeProfile(profile: SpeedProfile): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch {
    /* storage full or unavailable */
  }
}

/** Drop samples older than 7 days and cap the list. */
function prune(samples: SpeedSample[]): SpeedSample[] {
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return samples.filter((s) => s.t >= cutoff).slice(-500);
}

/** Record one GPS speed reading (m/s from the Geolocation API). */
export function recordSpeedSample(
  metersPerSecond: number | null,
  road = 'unknown'
): void {
  // Ignore stationary readings — they describe parking, not congestion.
  if (metersPerSecond == null || !Number.isFinite(metersPerSecond)) return;
  const kmh = metersPerSecond * 3.6;
  if (kmh < 3) return; // walking / crawling: no signal about road congestion
  if (kmh > 130) return; // implausible / GPS glitch

  const profile = readProfile();
  profile.samples = prune([...profile.samples, { speed: kmh, t: Date.now(), road }]);
  writeProfile(profile);
}

/* ------------------------------------------------------------------ */
/*  Congestion estimate                                                */
/* ------------------------------------------------------------------ */

/** Time-weighted mean of the samples (recent readings count for more). */
function decayedMean(samples: SpeedSample[]): number | null {
  if (!samples.length) return null;

  const now = Date.now();
  let weightSum = 0;
  let valueSum = 0;

  for (const s of samples) {
    const ageHours = (now - s.t) / (60 * 60 * 1000);
    const weight = Math.pow(0.5, ageHours / HALF_LIFE_HOURS);
    weightSum += weight;
    valueSum += s.speed * weight;
  }

  return weightSum > 0 ? valueSum / weightSum : null;
}

/**
 * Estimate congestion on the selected route.
 *
 * freeFlowMinutes comes from the routing engine. observedMinutes is how long
 * this corridor actually took the user previously, when known.
 */
export function estimateCongestion(
  freeFlowMinutes: number,
  previousTrips: number[] = []
): CongestionEstimate {
  const profile = readProfile();
  const mean = decayedMean(profile.samples);
  const observations = profile.samples.length;

  // Prefer real observations of this corridor.
  if (previousTrips.length >= 2 && freeFlowMinutes > 0) {
    const avg = previousTrips.reduce((a, b) => a + b, 0) / previousTrips.length;
    const factor = avg / freeFlowMinutes;
    return withLabel(factor, observations, mean);
  }

  // Fall back to the generic speed profile: compare the observed mean speed
  // against a typical free-flow speed for that kind of road.
  if (mean != null && observations >= 3) {
    // Assume ~70 km/h free-flow if we have no better reference; the factor is
    // relative, so this stays honest as a rough signal.
    const assumedFreeFlow = 70;
    const factor = assumedFreeFlow / Math.max(1, mean);
    return withLabel(factor, observations, mean);
  }

  return {
    factor: 1,
    level: 'unknown',
    label: 'No traffic data yet',
    observations,
    observedSpeed: mean,
  };
}

function withLabel(
  factor: number,
  observations: number,
  observedSpeed: number | null
): CongestionEstimate {
  let level: CongestionEstimate['level'];
  let label: string;

  if (factor < 1.15) {
    level = 'clear';
    label = 'Traffic looks clear';
  } else if (factor < 1.45) {
    level = 'moderate';
    label = 'Moderate traffic';
  } else {
    level = 'heavy';
    label = 'Heavy traffic';
  }

  return {
    factor: Math.round(factor * 100) / 100,
    level,
    label,
    observations,
    observedSpeed: observedSpeed == null ? null : Math.round(observedSpeed),
  };
}

export function congestionTone(level: CongestionEstimate['level']): string {
  switch (level) {
    case 'clear':
      return 'border-green-200 bg-green-50 text-green-800';
    case 'moderate':
      return 'border-amber-200 bg-amber-50 text-amber-800';
    case 'heavy':
      return 'border-red-200 bg-red-50 text-red-800';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-600';
  }
}

export function congestionDot(level: CongestionEstimate['level']): string {
  switch (level) {
    case 'clear':
      return 'bg-green-500';
    case 'moderate':
      return 'bg-amber-500';
    case 'heavy':
      return 'bg-red-500';
    default:
      return 'bg-slate-400';
  }
}

/* ------------------------------------------------------------------ */
/*  Trip history (per corridor)                                        */
/* ------------------------------------------------------------------ */

/** Round coords to ~100 m so the same corridor maps to one key. */
export function corridorKey(origin: string, destination: string): string {
  return `${origin.slice(0, 10)}|${destination.slice(0, 10)}`;
}

export function recordTrip(key: string, minutes: number): number[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const all: Record<string, number[]> = raw ? JSON.parse(raw) : {};
    const list = [...(all[key] ?? []), minutes].slice(-10);
    all[key] = list;
    localStorage.setItem(SESSION_KEY, JSON.stringify(all));
    return list;
  } catch {
    return [];
  }
}

export function getTrips(key: string): number[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const all: Record<string, number[]> = raw ? JSON.parse(raw) : {};
    return all[key] ?? [];
  } catch {
    return [];
  }
}

/* ------------------------------------------------------------------ */
/*  ETA maths                                                          */
/* ------------------------------------------------------------------ */

/** Add minutes to a Date and return the arrival Date. */
export function arrivalTime(departAt: Date, minutes: number): Date {
  return new Date(departAt.getTime() + minutes * 60_000);
}

/** "14:37" in the user's locale, 24h. */
export function formatClock(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** "Today" / "Tomorrow" / "Tue 14" relative to now. */
export function formatDay(d: Date, now: Date = new Date()): string {
  const startOf = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(now)) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
}

export interface DepartureOption {
  /** minutes from now */
  offsetMinutes: number;
  departAt: Date;
  arriveAt: Date;
  durationMinutes: number;
}

/**
 * Compare leaving now vs. leaving later. When we know the corridor runs
 * slower than free-flow during peak hours, the later option can genuinely be
 * faster — that is the point of the table.
 */
export function compareDepartures(
  freeFlowMinutes: number,
  congestion: CongestionEstimate,
  offsets = [0, 15, 30, 45]
): DepartureOption[] {
  const now = new Date();
  return offsets.map((offsetMinutes) => {
    const departAt = new Date(now.getTime() + offsetMinutes * 60_000);
    const peak = isPeakHour(departAt);

    // Heavier traffic assumed during peak hours, scaled by what we observed.
    let multiplier = 1;
    if (peak) {
      multiplier = 1 + Math.max(0, congestion.factor - 1) * 0.9;
    } else if (congestion.level === 'unknown') {
      multiplier = 1;
    }

    const durationMinutes = Math.round(freeFlowMinutes * multiplier);
    return {
      offsetMinutes,
      departAt,
      arriveAt: arrivalTime(departAt, durationMinutes),
      durationMinutes,
    };
  });
}

function isPeakHour(d: Date): boolean {
  const h = d.getHours() + d.getMinutes() / 60;
  const day = d.getDay();
  const weekend = day === 0 || day === 6;
  if (weekend) return false;
  return (h >= 7 && h <= 9.5) || (h >= 16 && h <= 19.5);
}

/* ------------------------------------------------------------------ */
/*  Live tracking session stats                                        */
/* ------------------------------------------------------------------ */

export interface TrackStats {
  points: number;
  /** metres */
  distance: number;
  /** seconds of actual movement */
  movingSeconds: number;
  /** seconds since tracking started */
  elapsedSeconds: number;
  /** km/h */
  currentSpeed: number | null;
  averageSpeed: number | null;
  maxSpeed: number;
  lastFixAt: number | null;
  /** previous fix, used to compute the step distance */
  lastCoords: { lat: number; lng: number } | null;
}

export const EMPTY_TRACK: TrackStats = {
  points: 0,
  distance: 0,
  movingSeconds: 0,
  elapsedSeconds: 0,
  currentSpeed: null,
  averageSpeed: null,
  maxSpeed: 0,
  lastFixAt: null,
  lastCoords: null,
};

/** Haversine distance in metres. */
export function distanceBetween(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number {
  const R = 6371e3;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Fold a new fix into the running session stats. */
export function updateTrack(
  stats: TrackStats,
  fix: { coords: { lat: number; lng: number }; speed: number | null; timestamp: number }
): TrackStats {
  const now = fix.timestamp || Date.now();

  const elapsedStep =
    stats.lastFixAt != null
      ? Math.min(60, Math.max(0, (now - stats.lastFixAt) / 1000))
      : 0;

  const next: TrackStats = {
    points: stats.points + 1,
    distance: stats.distance,
    movingSeconds: stats.movingSeconds,
    elapsedSeconds: stats.elapsedSeconds + elapsedStep,
    currentSpeed: fix.speed != null ? fix.speed * 3.6 : stats.currentSpeed,
    averageSpeed: stats.averageSpeed,
    maxSpeed: stats.maxSpeed,
    lastFixAt: now,
    lastCoords: fix.coords,
  };

  if (stats.lastCoords) {
    const step = distanceBetween(stats.lastCoords, fix.coords);
    // Ignore GPS jitter while stationary.
    if (step > 3) {
      next.distance += step;
      if (fix.speed != null && fix.speed > 0.8) {
        next.movingSeconds += elapsedStep;
        next.maxSpeed = Math.max(next.maxSpeed, fix.speed * 3.6);
      }
    }
  }

  next.averageSpeed =
    next.distance > 0 && next.elapsedSeconds > 0
      ? (next.distance / next.elapsedSeconds) * 3.6
      : null;

  return next;
}

/** "820 m" / "8.4 km" */
export function formatMetres(m: number): string {
  if (m < 1000) return `${Math.round(m / 10) * 10} m`;
  return `${(m / 1000).toFixed(2)} km`;
}

/** "0:12:30" */
export function formatStopwatch(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

export function clearTrafficData(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(PROFILE_KEY);
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}